// components/TeamColumns.jsx: Batch D single in-room roster.
//
// ONE roster, no double listing: a "Not on a team yet" pool ABOVE two team
// columns (Team A takes seats 1 and 3, Team B takes seats 2 and 4, partners
// sit across the table). Each column always renders exactly two seat rows:
// a player row when the seat is taken, or an "Open seat" row (plus "A bot
// will play here" when bot fill is on) when it is empty. The column's Join
// button sits below those rows, never over the text.
//
// Each player row carries avatar, name, host crown, connection state as
// text + colour (connected / reconnecting / away), ready state as text +
// mark ("Ready" / "Not ready"), and, for the host on other players, a Kick
// button with a confirm step (a second tap reading "Remove?").
//
// Backwards-compatible test ids: this roster is also the legacy
// occupant roster surface. Rows keep the room-occupant-* ids and classes
// (.room-occupant.is-you, .room-occupant-name, room-occupant-ready-<id>,
// room-occupant-kick-<id>, host-crown-badge) so the existing multiplayer,
// ready and kick specs address the same elements; the team-column ids
// (team-columns, team-column-A/B, team-count-A/B, team-join-A/B,
// team-unteamed) are unchanged for the team specs.

import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'preact/hooks';
import { AvatarCircle } from './AvatarCircle.jsx';
import { HostCrownBadge } from './HostCrownBadge.jsx';
import { canKickOccupant } from './host-control-logic.js';
import { canJoinTeam, teamLabel, rosterHeading } from './team-helpers.js';
import { rosterSeats } from './roster-helpers.js';
import { describeConnection } from './connection-helpers.js';
import { nextKickArmed } from './kick-confirm.js';
import { shouldStackTeams } from './team-layout.js';
import '../styles/room-lobby.css';
import '../styles/host-controls.css';
import '../styles/team-columns.css';

/** A single seated player row, shared by both columns and the pool. */
function PlayerRow({ occ, viewerIsHost, onKick, kickArmed, onArmKick }) {
  const showKick = canKickOccupant(occ, viewerIsHost) && typeof onKick === 'function';
  const armed = kickArmed === occ.occupant_id;
  const connection = describeConnection(occ.connection_status);
  return (
    <li
      class={`room-occupant team-occupant${occ.is_you ? ' is-you' : ''}`}
      data-testid={`room-occupant-${occ.occupant_id}`}
      data-is-you={occ.is_you ? 'true' : 'false'}
      data-is-host={occ.is_host ? 'true' : 'false'}
      data-connection-status={occ.connection_status || 'connected'}
      data-ready={occ.ready ? 'true' : 'false'}
      data-team={occ.team || 'none'}
    >
      <AvatarCircle color={/** @type {any} */ (occ.avatar_color)} name={occ.display_name} size={36} />
      <div class="room-occupant-meta">
        <div class="room-occupant-name-row">
          <span class="room-occupant-name team-occupant-name" data-testid="room-occupant-name" title={occ.display_name}>{occ.display_name}</span>
          {occ.is_you && <span class="room-occupant-badge" aria-label="this is you">you</span>}
          {occ.is_host && <HostCrownBadge />}
        </div>
        <span
          class={occ.ready ? 'room-occupant-ready ready' : 'room-occupant-ready not-ready'}
          data-testid={`room-occupant-ready-${occ.occupant_id}`}
          data-ready={occ.ready ? 'true' : 'false'}
          aria-label={occ.ready ? `${occ.display_name} is ready` : `${occ.display_name} is not ready`}
        >
          {occ.ready ? '✓ Ready' : '○ Not ready'}
        </span>
        <div class="room-occupant-status">
          <span
            class="room-occupant-dot"
            aria-hidden="true"
            style={{ background: connection.color }}
          />
          <span class="room-occupant-status-label" data-testid="room-occupant-connection">{connection.label}</span>
        </div>
      </div>
      {showKick && (
        <button
          type="button"
          class={armed ? 'room-occupant-kick armed' : 'room-occupant-kick'}
          data-testid={`room-occupant-kick-${occ.occupant_id}`}
          data-armed={armed ? 'true' : 'false'}
          onClick={() => (armed ? onKick(occ.occupant_id) : onArmKick(occ.occupant_id))}
          aria-label={armed ? `Remove ${occ.display_name}?` : `Remove ${occ.display_name} from the room`}
        >
          {armed ? 'Remove?' : 'Kick'}
        </button>
      )}
    </li>
  );
}

/** One team column: header + exactly two seat rows + its Join button. */
function TeamColumn({ team, seats, canJoin, onJoin, botFill, viewerIsHost, onKick, kickArmed, onArmKick, isYours }) {
  const label = teamLabel(team, isYours);
  const filled = seats.filter(Boolean).length;
  return (
    <div class={`team-column team-${team}`} data-testid={`team-column-${team}`} data-team={team}>
      <div class="team-column-header">
        <h3 class="team-column-title">
          {label}
          {/* B3b D5: the column keeps its "Team A" name after the viewer
              picks a side; their own column gets a small tag instead. */}
          {isYours === true && (
            <span class="team-yours-tag" data-testid={`team-yours-tag-${team}`}>your team</span>
          )}
        </h3>
        <span class="team-column-count" data-testid={`team-count-${team}`}>{filled}/2</span>
      </div>
      <ul class="team-occupant-list" role="list" data-testid={`team-seats-${team}`}>
        {seats.map((occ, idx) => occ ? (
          <PlayerRow
            key={occ.occupant_id}
            occ={occ}
            viewerIsHost={viewerIsHost}
            onKick={onKick}
            kickArmed={kickArmed}
            onArmKick={onArmKick}
          />
        ) : (
          <li
            key={`open-${team}-${idx}`}
            class="team-seat-open"
            data-testid={`team-seat-open-${team}-${idx + 1}`}
          >
            <div class="team-seat-open-text">
              <span class="team-seat-open-label">Open seat</span>
              {botFill && <span class="team-seat-open-bot">A bot will play here</span>}
            </div>
          </li>
        ))}
      </ul>
      <button
        type="button"
        class="team-join-btn"
        data-testid={`team-join-${team}`}
        onClick={() => onJoin(team)}
        disabled={!canJoin}
        aria-disabled={!canJoin}
        title={canJoin ? `Join ${label}` : `${label} is unavailable`}
      >
        Join {label}
      </button>
    </div>
  );
}

/**
 * @param {{
 *   occupants: Array<{
 *     occupant_id: string, display_name: string, avatar_color: string,
 *     is_host: boolean, is_you: boolean,
 *     connection_status: 'connected'|'lagging'|'disconnected',
 *     ready: boolean,
 *     team: null|'A'|'B',
 *   }>,
 *   myOccupantId: string|null,
 *   onJoinTeam: (team: 'A'|'B') => void,
 *   viewerIsHost?: boolean,
 *   onKick?: (occupant_id: string) => void,
 *   botFill?: boolean,
 * }} props
 */
export function TeamColumns({
  occupants,
  myOccupantId,
  onJoinTeam,
  viewerIsHost = false,
  onKick,
  botFill = false,
}) {
  const seats = rosterSeats(occupants);
  const [kickArmed, setKickArmed] = useState(null);
  const handleArmKick = useCallback((id) => setKickArmed((current) => nextKickArmed(current, { type: 'arm', id })), []);
  useEffect(() => {
    setKickArmed((current) => nextKickArmed(current, { type: 'occupants', ids: occupants.map((o) => o.occupant_id) }));
  }, [occupants]);
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') setKickArmed((current) => nextKickArmed(current, { type: 'escape' }));
    };
    const onClick = (event) => {
      if (event.target.closest('button, input, select, a') && !event.target.closest('[data-testid^="room-occupant-kick-"]')) {
        setKickArmed((current) => nextKickArmed(current, { type: 'other-control' }));
      }
    };
    // U2: keyboard changes never fire click, so a select/checkbox/radio
    // picked from the keyboard (or any scripted input) would leave Kick
    // armed. Any change or input outside the Kick buttons disarms too,
    // however it was triggered.
    const onFieldEdit = (event) => {
      if (event.target.closest && event.target.closest('[data-testid^="room-occupant-kick-"]')) return;
      setKickArmed((current) => nextKickArmed(current, { type: 'other-control' }));
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('click', onClick);
    document.addEventListener('change', onFieldEdit);
    document.addEventListener('input', onFieldEdit);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onClick);
      document.removeEventListener('change', onFieldEdit);
      document.removeEventListener('input', onFieldEdit);
    };
  }, []);
  useEffect(() => {
    if (!kickArmed) return undefined;
    const timer = window.setTimeout(() => setKickArmed((current) => nextKickArmed(current, { type: 'timeout' })), 5000);
    return () => window.clearTimeout(timer);
  }, [kickArmed]);
  // Which column is the viewer's? null until they pick a side.
  const myTeam = occupants.find((o) => o.occupant_id === myOccupantId)?.team ?? null;
  // U1: measure the roster's own width and stack the columns whenever a
  // side-by-side seat row would be narrower than SEAT_MIN_ROW_WIDTH.
  // The grid also carries data-layout so the CSS rule shares this one
  // helper; the container query in team-columns.css repeats the same
  // ROSTER_STACK_BELOW threshold as a no-JS path.
  /** @type {{ current: HTMLDivElement | null }} */
  const rosterRef = useRef(null);
  const [stacked, setStacked] = useState(false);
  // T2: measure in a layout effect so the data-layout attribute is
  // correct before the first paint (useEffect would run after paint
  // and flash data-layout="side" for about one frame on narrow rooms).
  useLayoutEffect(() => {
    const el = rosterRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setStacked(shouldStackTeams(el.clientWidth));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <section class="team-columns" data-testid="team-columns" aria-label="Room roster">
      <h2 class="team-columns-heading">{rosterHeading(occupants.length)}</h2>
      <p class="team-partners-note" data-testid="team-partners-note">Team A takes seats 1 and 3, Team B takes seats 2 and 4 — partners sit across the table.</p>
      {/* Existing occupant roster surface: the multiplayer specs
          address the roster through this id; the rows below are the only
          listing, so everyone still appears exactly once. */}
      <div class="team-roster-list" data-testid="room-occupants" ref={rosterRef}>
        {seats.unteamed.length > 0 && (
          <div class="team-unteamed" data-testid="team-unteamed">
            <h3 class="team-unteamed-title">Not on a team yet</h3>
            <ul class="team-occupant-list" role="list">
              {seats.unteamed.map((occ) => (
                <PlayerRow
                  key={occ.occupant_id}
                  occ={occ}
                  viewerIsHost={viewerIsHost}
                  onKick={onKick}
                  kickArmed={kickArmed}
                  onArmKick={handleArmKick}
                />
              ))}
            </ul>
          </div>
        )}
        <div class="team-columns-grid" data-testid="team-columns-grid" data-layout={stacked ? 'stacked' : 'side'}>
          {/** @type {Array<'A'|'B'>} */ (['A', 'B']).map((team) => (
            <TeamColumn
              key={team}
              team={team}
              seats={seats[team]}
              canJoin={canJoinTeam(occupants, myOccupantId, team)}
              onJoin={onJoinTeam}
              botFill={botFill}
              viewerIsHost={viewerIsHost}
              onKick={onKick}
              kickArmed={kickArmed}
              onArmKick={handleArmKick}
              isYours={myTeam == null ? null : myTeam === team}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
